// src/pages/TrackCreation/Step1.tsx
import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { DropdownOption, RouteParams, StopLanguageProps } from "../../../../../types/screensTypes";
import Button from "../../../../../components/ui/Button";
import { saveStop, getStopLanguages } from "../../../../../api";
import Dropdown from "../../../../../components/common/Dropdown";
import TitleSubtitle from "../../../../../components/common/TitleSubtitle";
import { useTrackCreation } from "../../../../../context/TrackCreationContext";
import { useGlobalState } from "../../../../../context/useGlobalState";
import { LanguageOption } from "../../../../../types/types";
import stopEdit from "./StopEdit.module.css";
import stepStyles from "../../createStep.module.css";

const StopLanguage: React.FC<StopLanguageProps> = ({ navigateToNext }) => {
    const { trackData } = useTrackCreation();
    const { data, loading, error } = useGlobalState();
    const [selectedContentLanguage, setSelectedContentLanguage] = useState<string | null>(null);
    const { wptId } = useParams<RouteParams>(); // Get the project ID from the URL
    const [existingLanguages, setExistingLanguages] = useState<string[]>([]);
    const [showAddLanguageDropdown, setShowAddLanguageDropdown] = useState<boolean>(false);
    const { t: rawT } = useTranslation();
    const t = rawT as any;

    const saveStopAPI = async (): Promise<void> => {
        const token = localStorage.getItem("token"); // Retrieve token (or other logic to get it)
        const trackId = trackData.trackId; // Get the track ID from the context

        if (!wptId || !trackId || !selectedContentLanguage || !token) {
            return;
        }

        await saveStop({
            token,
            trackId: trackId,
            waypointId: wptId,
            language: selectedContentLanguage,
        });

        // It is assumed that wptId is defined since it was checked before calling the API
        navigateToNext(wptId, selectedContentLanguage || "");
    };

    const getExistingLanguagesAPI = async (): Promise<void> => {
        const token = localStorage.getItem("token");
        if (!token || !wptId) {
            return;
        }
        const languages = await getStopLanguages({ token, waypointId: wptId });

        const languagesArray: string[] = languages.map((lang: { id: string }) => lang.id);

        setExistingLanguages(languagesArray);
    };

    useEffect(() => {
        if (wptId && wptId !== "") {
            getExistingLanguagesAPI();
        }
    }, [wptId]);

    // Ensure that data.languagesAI is defined; fallback to empty array if not.
    const allLanguages: LanguageOption[] = data.languagesAI || [];

    const languagesFiltered: DropdownOption[] = [
        ...allLanguages.filter((lang) => existingLanguages.includes(lang.value)),
        { label: t("addANewLanguage"), value: "add-new-language" }, // Add the "Add a new language" option
    ];

    const languagesUnfiltered: LanguageOption[] = allLanguages.filter(
        (lang) => !existingLanguages.includes(lang.value),
    );

    const handleLanguageChange = (selectedOption: string) => {
        if (selectedOption === "add-new-language") {
            setShowAddLanguageDropdown(true); // Show the second dropdown
            setSelectedContentLanguage(null); // Reset the selected language
        } else {
            setShowAddLanguageDropdown(false); // Hide the second dropdown
            setSelectedContentLanguage(selectedOption); // Set the selected language
        }
    };

    return (
        <div className={`experience-stop-edit ${stopEdit.root}`}>
            <TitleSubtitle title={t("selectTheLanguageOfYourStop")} subtitle={""} layout="step" />
            <div className={`experience-stop-field ${stopEdit.field}`}>
                <h5 className={`experience-stop-field-label ${stopEdit.fieldLabel}`}>{t("existingLanguages")}</h5>
                <Dropdown
                    label=""
                    options={languagesFiltered}
                    onChange={handleLanguageChange}
                    loading={loading}
                    error={error ? true : false}
                    placeholder={t("language")}
                    value={selectedContentLanguage}
                />
            </div>
            {showAddLanguageDropdown && (
                <div className={`experience-stop-field ${stopEdit.field}`}>
                    <h5 className={`experience-stop-field-label ${stopEdit.fieldLabel}`}>{t("selectANewLanguage")}</h5>
                    <Dropdown
                        label=""
                        options={languagesUnfiltered}
                        onChange={setSelectedContentLanguage}
                        loading={loading}
                        error={error ? true : false}
                        placeholder={t("language")}
                        value={selectedContentLanguage}
                    />
                </div>
            )}
            <div className={`${stepStyles.stepButton} ${stepStyles.footer}`}>
                <Button
                    variant="primary"
                    shape="pill"
                    className={stepStyles.continueBtn}
                    label={t("continue")}
                    onClick={saveStopAPI}
                />
            </div>
        </div>
    );
};

export default StopLanguage;
